import { get, set, uniqueId } from 'lodash-es';
import { FEATURE_FLAG, getFeatureFlag } from '@phoenix/helpers/feature-flag-helper';
import * as formHelper from '@phoenix/helpers/form-helper';
import LoginForm from '@phoenix/mixins/components/form/LoginForm';

export default {
  mixins: [LoginForm],

  props: {
    tokenRetryTimeout: {
      type: Number,
      default: 5,
    },
  },

  data() {
    return {
      // represent the current step, which can be one of: start, phone, token, phonePassword, emailPassword
      currentStep: this.originalFirstStep(),
      timeLeftBeforeTokenRetry: 0,
      phoneTokenRetryCountdown: null,
      fields: {
        retryCountdown: {
          name: 'retryCountdown',
        },
        emailOrPhone: {
          id: uniqueId('emailOrPhone_'),
          name: 'emailOrPhone',
          label: this.$t(getFeatureFlag(FEATURE_FLAG.GEMINI_CN) ? 'Form.emailOrPhone' : 'Form.email'),
          value: '',
          required: true,
          type: getFeatureFlag(FEATURE_FLAG.GEMINI_CN) ? 'text' : 'email',
        },
        password: {
          id: uniqueId('password_'),
          name: 'password',
          label: this.$t('Form.password'),
          value: '',
          required: true,
          type: 'password',
        },
        phoneToken: {
          id: uniqueId('phoneToken_'),
          name: 'phoneToken',
          label: this.$t('Form.phoneToken'),
          value: '',
          required: true,
        },
      },
    };
  },

  watch: {
    emailOrPhoneType(type) {
      this.fields.emailOrPhone.type = type;
    },

    currentStep(step) {
      set(this.fields, ['emailOrPhone', 'disabled'], step !== this.originalFirstStep());
    },

    'fields.retryCountdown.errors'(value) {
      const retryCountdown = localStorage.getItem('retryCountdown');

      if (!retryCountdown && value) {
        window.localStorage.setItem('retryCountdown', new Date().toUTCString());

        this.startPhoneTokenCountdown();
      }
    },
  },

  computed: {
    emailOrPhoneType() {
      if (!getFeatureFlag(FEATURE_FLAG.GEMINI_CN)) {
        return 'email';
      }
      switch (this.currentStep) {
        case 'phone':
        case 'phonePassword':
        case 'phoneToken':
          return 'phone';
        case 'emailPassword':
          return 'email';
      }

      return 'text';
    },

    hasPhoneTokenTooManyRetries() {
      return this.timeLeftBeforeTokenRetry !== 0;
    },

    showNextButton() {
      return this.currentStep === 'start';
    },

    showTokenOrPasswordButtons() {
      return this.currentStep === 'phone';
    },

    showPasswordField() {
      return this.currentStep === 'phonePassword' || this.currentStep === 'emailPassword';
    },

    showTokenField() {
      return this.currentStep === 'phoneToken';
    },

    showLoginButton() {
      return this.showPasswordField || this.showTokenField;
    },

    isNextButtonDisabled() {
      return !this.fields.emailOrPhone.value;
    },

    getPasswordHelper() {
      const trackingData = this.getPasswordHelperDataTracking();
      const trackingAttribute =
        Object.keys(trackingData).length > 0 ? ` data-tracking='${JSON.stringify(trackingData)}'` : '';

      return `
        <a href="${this.getRetrievePasswordUrl()}" class="${this.getRetrievePasswordLinkClass()}"${trackingAttribute}>
          ${this.$t('MyAccount.forgotYourPassword')}
        </a>
      `;
    },
  },

  mounted() {
    this.fields.password.help = this.getPasswordHelper;

    if (localStorage.getItem('retryCountdown') && this.phoneTokenRetryCountdown === null) {
      this.startPhoneTokenCountdown();
    }
  },

  methods: {
    startPhoneTokenCountdown() {
      this.timeLeftBeforeTokenRetry =
        this.tokenRetryTimeout - Math.floor((new Date() - new Date(localStorage.getItem('retryCountdown'))) / 60000);

      this.phoneTokenRetryCountdown = setInterval(() => {
        this.timeLeftBeforeTokenRetry -= 1;
        if (this.timeLeftBeforeTokenRetry < 0) {
          window.localStorage.removeItem('retryCountdown');
          this.timeLeftBeforeTokenRetry = 0;
          clearInterval(this.phoneTokenRetryCountdown);
        }
      }, 60000);
    },

    getPasswordHelperDataTracking() {
      return {};
    },

    originalFirstStep() {
      return getFeatureFlag(FEATURE_FLAG.GEMINI_CN) ? 'start' : 'emailPassword';
    },

    next() {
      this.currentStep = formHelper.isEmail(get(this, ['fields', 'emailOrPhone', 'value'], ''))
        ? 'emailPassword'
        : 'phone';
    },

    back() {
      // we'll reset the token input field
      set(this.fields.token, 'value', '');
      this.currentStep = this.originalFirstStep();
    },

    backToPhoneStep() {
      this.currentStep = 'phone';
    },

    async sendToken() {
      Toolkit.helpers.buttons.addLoadingState(this.$refs.sendTokenPhoneButton);
      await this.$refs.sendPhoneTokenForm.submitForm();
    },

    sendTokenSuccess() {
      set(this.fields, ['phoneToken', 'value'], '');
      this.currentStep = 'phoneToken';

      Toolkit.helpers.buttons.removeLoadingState(this.$refs.sendTokenPhoneButton);
    },

    sendTokenFail() {
      Toolkit.helpers.buttons.removeLoadingState(this.$refs.sendTokenPhoneButton);
    },

    usePassword() {
      this.currentStep = 'phonePassword';
    },

    getRetrievePasswordUrl() {
      let url = '';
      try {
        url = window.route('retrieve_password_page');
      } catch {
        // Route is optional depending on the current localisation.
      }
      return url;
    },
  },
};
